import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, Quote } from 'lucide-react';

interface Testimonial {
  name: string;
  role: string;
  quote: string;
  photo?: string;
}

const TESTIMONIALS: Testimonial[] = [
  {
    name: 'Amalia López Acera',
    role: 'Jefa de Comunicación · Agencia Valenciana Antifraude',
    photo: 'https://novagob.org/wp-content/uploads/2019/05/Amalia-Lopez-Acera.jpg',
    quote:
      'Los Premios NovaGob me han ayudado a potenciar mi marca personal y me han facilitado conocer a otras personas del sector público y poder colaborar en distintos proyectos e iniciativas.',
  },
  {
    name: 'Eulalia Plá',
    role: 'Subdirectora General de Recerca, Innovació i Desenvolupament',
    photo: 'https://novagob.org/wp-content/uploads/2025/03/EulaliaPla.jpg',
    quote:
      'Visibilizar un proyecto de trabajo transversal tanto a nivel de la institución (EAPC) como de forma interdepartamental. Dar proyección a un proyecto innovador y compartir la buena práctica.',
  },
  {
    name: 'José Antonio Eusamio',
    role: 'Subdirección General de Impulso de la Digitalización de la Administración',
    photo: 'https://novagob.org/wp-content/uploads/2017/04/juan-antonio-eusamio.jpg',
    quote:
      'Lo mejor es que presentarte a los Premios NovaGob te obliga a documentar tu proyecto, y eso te hace mejorar y ser más cuidadoso. Recuerda que el viaje es parte del proceso.',
  },
  {
    name: 'Pilar Moreno',
    role: 'Técnica de Formación · Agencia Valenciana Antifraude',
    photo: 'https://novagob.org/wp-content/uploads/2025/03/PilarMoreno.jpg',
    quote:
      'Este reconocimiento representa la emoción de saber que la ilusión, el dinamismo y el compromiso con el interés general también cuentan y se valoran en lo público. Haber participado ha sido un nuevo impulso clave en mi crecimiento personal y profesional. Crecer y creer en mí.',
  },
  {
    name: 'Concepción Campos',
    role: 'Presidenta Mujeres SP',
    photo: 'https://novagob.org/wp-content/uploads/2024/03/conchi-camposMSP.jpg',
    quote:
      'Los Premios NovaGob ponen de relieve el trabajo que grandes profesionales están desarrollando para la mejora de los servicios públicos y para la auténtica transformación en una Administración del S. XXI.',
  },
  {
    name: 'Joaquín Villar',
    role: 'Jefe del Departamento de Internacionalización y Prospectiva · Agencia Andaluza de la Energía',
    photo: 'https://novagob.org/wp-content/uploads/2025/03/JoaquinVillar.webp',
    quote:
      'Este premio ha supuesto un importante reconocimiento a la labor que realiza la Agencia Andaluza de la Energía. Conocer las mejores prácticas y las soluciones innovadoras que se están desarrollando es un reto importante.',
  },
  {
    name: 'Diana Arenas',
    role: 'CEO de Fundación SandBox',
    photo: 'https://novagob.org/wp-content/uploads/2025/03/DianaArenas-scaled.jpg',
    quote:
      'El premio recibido fue un honor y un recordatorio del impacto que tenemos las mujeres en la transformación del sector público. Estos premios son una oportunidad para conectar con una comunidad comprometida con la mejora del sector público.',
  },
  {
    name: 'María Pilar Batet',
    role: 'Jefa de Servicio de Contratación · Diputación de Castellón',
    photo: 'https://novagob.org/wp-content/uploads/2025/03/PilarBatet.jpg',
    quote:
      'El premio recibido es la prueba de que cuando creemos en el cambio y trabajamos con pasión, el esfuerzo tiene recompensa. Innovar no siempre es fácil, pero cada idea, cada desafío superado y cada paso adelante supone un impacto real.',
  },
  {
    name: 'Víctor Almonacid',
    role: 'Directivo Público Profesional',
    photo: 'https://novagob.org/wp-content/uploads/2025/03/VictorAlmonacid.png',
    quote:
      'Presentarse a los Premios NovaGob es poner en valor tu proyecto, ponerlo en el mapa nada menos que a nivel internacional. Las buenas prácticas se deben compartir, y los premios son la mejor manera de visibilizarlas.',
  },
  {
    name: 'Fermín Cerezo',
    role: 'Jefe de Servicio de Innovación · Ayuntamiento de València',
    photo: 'https://novagob.org/wp-content/uploads/2024/03/fermin-cerezo.jpg',
    quote:
      'Participar en los Premios NovaGob ayuda a darnos cuenta del trabajo realizado, de sus puntos fuertes, de lo conseguido, de los resultados e impactos y, por supuesto, de los principales aprendizajes que hemos obtenido en el camino de la innovación.',
  },
];

const getInitials = (name: string) =>
  name
    .split(' ')
    .filter((w) => w.length > 0)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('');

const TestimonialsSection: React.FC = () => {
  const trackRef = useRef<HTMLDivElement>(null);

  const scrollByOne = useCallback((dir: 1 | -1) => {
    const track = trackRef.current;
    if (!track) return;
    const firstSlide = track.firstElementChild as HTMLElement | null;
    if (!firstSlide) return;
    const delta = firstSlide.clientWidth + 20;

    const { scrollLeft, scrollWidth, clientWidth } = track;
    const atStart = scrollLeft <= 2;
    const atEnd = scrollLeft + clientWidth >= scrollWidth - 2;

    if (dir === 1 && atEnd) {
      track.scrollTo({ left: 0, behavior: 'smooth' });
    } else if (dir === -1 && atStart) {
      track.scrollTo({ left: scrollWidth, behavior: 'smooth' });
    } else {
      track.scrollBy({ left: dir * delta, behavior: 'smooth' });
    }
  }, []);

  // Autoplay
  const [paused, setPaused] = useState(false);
  useEffect(() => {
    if (paused) return;
    const t = setInterval(() => scrollByOne(1), 7000);
    return () => clearInterval(t);
  }, [paused, scrollByOne]);

  return (
    <section
      id="testimonios"
      className="landing-section landing-testimonials"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="landing-container">
        <div className="landing-testimonials-header">
          <h2 className="landing-section-title text-gold-shine">
            ¿Por qué participar?
          </h2>
          <p className="landing-section-lead">
            Lo cuentan quienes ya han pasado por el proceso y se han llevado un
            reconocimiento.
          </p>
          <div className="landing-testimonials-controls">
            <button
              type="button"
              className="cj-arrow"
              onClick={() => scrollByOne(-1)}
              aria-label="Testimonio anterior"
            >
              <ChevronLeft size={20} />
            </button>
            <button
              type="button"
              className="cj-arrow"
              onClick={() => scrollByOne(1)}
              aria-label="Siguiente testimonio"
            >
              <ChevronRight size={20} />
            </button>
          </div>
        </div>

        <div className="landing-testimonials-carousel">
          <div className="landing-testimonials-track" ref={trackRef}>
            {TESTIMONIALS.map((t) => (
              <article key={t.name} className="landing-testimonial">
                <Quote
                  className="landing-testimonial-quoteicon"
                  size={90}
                  aria-hidden="true"
                />
                <p className="landing-testimonial-quote">"{t.quote}"</p>
                <div className="landing-testimonial-author">
                  <div className="landing-testimonial-photo">
                    {t.photo ? (
                      <img src={t.photo} alt={t.name} />
                    ) : (
                      <span>{getInitials(t.name)}</span>
                    )}
                  </div>
                  <div>
                    <div className="landing-testimonial-name">{t.name}</div>
                    <div className="landing-testimonial-role">{t.role}</div>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
